/**
 * Утилита для управления полноэкранным режимом
 */
import { errorLogger } from '../errorLogger.js';

/**
 * Проверяет, поддерживается ли Fullscreen API
 */
function isFullscreenSupported() {
    return !!(
        document.fullscreenEnabled ||
        document.webkitFullscreenEnabled ||
        document.mozFullScreenEnabled ||
        document.msFullscreenEnabled
    );
}

/**
 * Проверяет, находится ли документ в полноэкранном режиме
 */
function isFullscreen() {
    return !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
    );
}

/**
 * Проверяет, запущено ли приложение в Telegram Desktop
 */
function isTelegramDesktop() {
    // Проверяем наличие Telegram WebApp API
    if (typeof window.Telegram !== 'undefined' && window.Telegram.WebApp) {
        // Проверяем userAgent на наличие TelegramDesktop
        const userAgent = navigator.userAgent || '';
        return userAgent.includes('TelegramDesktop') || userAgent.includes('Telegram Desktop');
    }
    return false;
}

/**
 * Входит в полноэкранный режим
 * ВАЖНО: Автоматический разворот на весь экран работает только для Telegram Desktop
 */
async function enterFullscreen() {
    // Проверяем, что мы в Telegram Desktop, иначе не входим в полноэкранный режим
    if (!isTelegramDesktop()) {
        return false;
    }
    
    if (!isFullscreenSupported()) {
        console.warn('Fullscreen API не поддерживается в этом браузере');
        return false;
    }

    if (isFullscreen()) {
        return true; // Уже в полноэкранном режиме
    }

    try {
        const element = document.documentElement;
        
        if (element.requestFullscreen) {
            await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
            await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
            await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
            await element.msRequestFullscreen();
        } else {
            console.warn('Метод requestFullscreen не найден');
            return false;
        }
        
        return true;
    } catch (error) {
        console.error('Ошибка при входе в полноэкранный режим:', error);
        
        // Отправляем ошибку на сервер
        errorLogger.logError({
            error_type: 'fullscreen_error',
            message: `Ошибка при входе в полноэкранный режим: ${error.message || String(error)}`,
            stack: error.stack,
            url: window.location.href,
            additional_info: {
                error_name: error.name,
                fullscreen_supported: isFullscreenSupported(),
                already_fullscreen: isFullscreen()
            }
        });
        
        return false;
    }
}

/**
 * Выходит из полноэкранного режима
 */
async function exitFullscreen() {
    if (!isFullscreen()) {
        return true; // Уже не в полноэкранном режиме
    }

    try {
        if (document.exitFullscreen) {
            await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
            await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
            await document.msExitFullscreen();
        } else {
            console.warn('Метод exitFullscreen не найден');
            return false;
        }
        
        return true;
    } catch (error) {
        console.error('Ошибка при выходе из полноэкранного режима:', error);
        
        // Отправляем ошибку на сервер
        errorLogger.logError({
            error_type: 'fullscreen_error',
            message: `Ошибка при выходе из полноэкранного режима: ${error.message || String(error)}`,
            stack: error.stack,
            url: window.location.href,
            additional_info: {
                error_name: error.name,
                fullscreen_supported: isFullscreenSupported(),
                was_fullscreen: isFullscreen()
            }
        });
        
        return false;
    }
}

/**
 * Переключает полноэкранный режим
 */
async function toggleFullscreen() {
    if (isFullscreen()) {
        return await exitFullscreen();
    } else {
        return await enterFullscreen();
    }
}

export {
    isFullscreenSupported,
    isFullscreen,
    isTelegramDesktop,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen
};

